import React, { useContext } from 'react';
import { Button, Flex, InputNumber, Select, Switch } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import NozomiEventConfigContext from '@totem/components/settings/sections/nozomi/eventConfiguration/nozomiEventConfigContext';
import {
  NozomiEventConfiguration,
  RiskItem,
  RiskItems,
} from '@totem/components/settings/sections/nozomi/eventConfiguration/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  canEdit: boolean;
  riskItems: RiskItems;
};

const RiskTable = ({ riskItems, canEdit }: Props) => {
  const { loading, data, setData, updateRisk } = useContext(
    NozomiEventConfigContext,
  );

  const addRisk = () => {
    for (let idx = 1; idx < 11; idx++) {
      if (riskItems.findIndex((item) => item.risk === idx) < 0) {
        const newRiskItem: RiskItem = {
          risk: idx,
          import: false,
          eventSeverity: 2,
        };
        const updatedNozomiConfig: NozomiEventConfiguration = {
          ...data,
          organization: {
            ...data.organization,
            riskConfiguration: [
              ...data.organization.riskConfiguration,
              newRiskItem,
            ],
          },
        };

        setData(updatedNozomiConfig);
        return;
      }
    }
  };

  const removeRisk = (old: RiskItem) => {
    const updatedNozomiConfig: NozomiEventConfiguration = {
      ...data,
      organization: {
        ...data.organization,
        riskConfiguration: data.organization.riskConfiguration.filter(
          (item) => item.risk !== old.risk,
        ),
      },
    };

    setData(updatedNozomiConfig);
  };

  const columns: ColumnProps<RiskItem>[] = [
    {
      title: 'Risk',
      dataIndex: 'risk',
      key: 'risk',
      render: (_, record: RiskItem) => (
        <InputNumber
          value={record.risk}
          min={1}
          max={10}
          disabled={!canEdit}
          onChange={(newValue) => {
            updateRisk(record, { ...record, risk: newValue });
          }}
        />
      ),
    },
    {
      title: 'Import',
      dataIndex: 'import',
      key: 'import',
      render: (_, record: RiskItem) => (
        <Switch
          value={record.import}
          disabled={!canEdit}
          onChange={(newValue) => {
            updateRisk(record, { ...record, import: newValue });
          }}
        />
      ),
    },
    {
      title: 'Event Severity',
      dataIndex: 'eventSeverity',
      key: 'eventSeverity',
      render: (_, record: RiskItem) => (
        <Select
          value={record.eventSeverity}
          disabled={!canEdit}
          options={[
            {
              label: 'Ok',
              value: 0,
            },
            {
              label: 'Information',
              value: 1,
            },
            {
              label: 'Warning',
              value: 2,
            },
            {
              label: 'Minor',
              value: 3,
            },
            {
              label: 'Major',
              value: 4,
            },
            {
              label: 'Critical',
              value: 5,
            },
          ]}
          onChange={(newValue) => {
            updateRisk(record, { ...record, eventSeverity: newValue });
          }}
        />
      ),
    },
    {
      title: (
        <Flex justify="space-between">
          <div>Action</div>
          <div>
            {canEdit && (
              <Button
                shape={'circle'}
                icon={<IBDIcon name={'Add'} />}
                onClick={() => addRisk()}
              />
            )}
          </div>
        </Flex>
      ),
      width: '124px',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: RiskItem) => {
        if (canEdit) {
          return (
            <Button
              shape={'circle'}
              icon={<IBDIcon name={'Delete'} />}
              onClick={() => removeRisk(record)}
            />
          );
        }

        return <span />;
      },
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(riskItems) ? riskItems : []}
      loading={loading}
      pagination={{
        current: 1,
        pageSize: 1000,
      }}
      rowKey={(record) => record.risk}
    />
  );
};

export default RiskTable;
